/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/ai-autonomous.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


/**
 * Why a detection did or did not escalate. `withheld` is the one a review reads hardest: the
 * evidence cleared the threshold and the mode held it back, so those are exactly the rows that
 * will escalate on the day escalation is turned on. Folding it into either neighbour would bury
 * the only rows an observation period is really about. It is also the verdict for a detection
 * whose escalation opened nothing — the row then carries the reason in `rationale` rather than
 * claiming an escalation that did not happen.
 */
export type DetectionVerdict = 'escalated' | 'withheld' | 'below_threshold';

export const DETECTION_VERDICTS: readonly DetectionVerdict[] = ['escalated', 'withheld', 'below_threshold'];

export function isDetectionVerdict(value: unknown): value is DetectionVerdict {
  return (['escalated', 'withheld', 'below_threshold'] as readonly unknown[]).includes(value);
}

/**
 * `shadow` is observing — detections are recorded and no incident is opened. `live` is escalating.
 * The stored values are unchanged from 1.0 so no row, CHECK or client has to move; what changed is
 * what crossing between them does. It used to gate acting on a customer's configuration; it now
 * gates whether an incident is opened and somebody is paged.
 */
export type Mode = 'shadow' | 'live';

export const MODES: readonly Mode[] = ['shadow', 'live'];

export function isMode(value: unknown): value is Mode {
  return (['shadow', 'live'] as readonly unknown[]).includes(value);
}

export interface Detection {
  readonly id: string;
  /** The rule binding this detection is about, and the incident's subject. */
  readonly binding_id: string;
  /**
   * Carried on the row rather than joined on read, so the log reads without a lookup and a renamed
   * rule does not rewrite history.
   */
  readonly rule_name: string;
  /**
   * `null` when the binding's window covers more than one dataset. A binding can resolve to many
   * through its selectors, so naming one would misdescribe it.
   */
  readonly dataset_id: string | null;
  /**
   * The qualified name, when there is exactly one. `null` rather than a placeholder, for the same
   * reason as `dataset_id` — and it is what stops a notification printing a UUID at the one place
   * a human reads it.
   */
  readonly dataset_label: string | null;
  /**
   * `(failing runs / runs) * min(1, runs / 12)`. The discount is the insufficient-evidence
   * safeguard: two failures out of two scores 0.17, not 1.0, so a flaky morning cannot raise an
   * incident by itself.
   */
  readonly confidence: number;
  /**
   * The bar this detection was judged against, stored per row rather than read from configuration
   * — so a detection cited in a decision stays interpretable if the bar moves.
   */
  readonly threshold: number;
  readonly verdict: DetectionVerdict;
  readonly mode: Mode;
  /**
   * Whether an incident was opened or joined. False in observing mode by construction rather than
   * by configuration, held by a substrate CHECK.
   */
  readonly escalated: boolean;
  /**
   * The incident this detection opened or joined. Non-null exactly when `escalated` is true, held
   * by a CHECK in both directions — an escalation that named no incident would be unfollowable,
   * and an incident on a withheld row would claim an escalation the mode refused.
   */
  readonly incident_id: string | null;
  /**
   * **Tri-state, and the third state is the point.** `null` means no notification was attempted —
   * observing, or below the threshold. `false` means it was evaluated and delivered nowhere, which
   * **includes a tenant whose routing rules send this severity nowhere**. `true` means at least
   * one destination took it. A boolean would render the first two identically, and "your routing
   * sends this nowhere" is an operator finding rather than an absence.
   */
  readonly notified: boolean | null;
  /**
   * The platform's own sentence about the delivery outcome, host-written. `null` when nothing was
   * attempted. Never reports a message as sent when it was not.
   */
  readonly notify_detail: string | null;
  /** Successful executions inside the window. At most 12. */
  readonly runs: number;
  /** How many of those produced violations. Never greater than `runs`, held by a CHECK. */
  readonly failing_runs: number;
  /** Total violating rows across the window. */
  readonly violations: number;
  /**
   * The arithmetic in words, host-written, quoting only counts the engine measured. It says the
   * binding kept finding violations and stops there. It deliberately does **not** say the rule is
   * wrong: bad data and a misconfigured rule are indistinguishable from execution counts, and
   * naming one would put a conclusion in the record that the evidence does not carry.
   */
  readonly rationale: string;
  /**
   * The subject on the request, or `system:autonomous-sweep` for the timer — which has no
   * principal, and saying so is more honest than attributing a sweep to a person.
   */
  readonly detected_by: string;
  readonly detected_at: string;
}

export function isDetection(value: unknown): value is Detection {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['id'] === 'string' &&
    typeof candidate['binding_id'] === 'string' &&
    typeof candidate['rule_name'] === 'string' &&
    (typeof candidate['dataset_id'] === 'string' || candidate['dataset_id'] === null) &&
    (typeof candidate['dataset_label'] === 'string' || candidate['dataset_label'] === null) &&
    typeof candidate['confidence'] === 'number' &&
    typeof candidate['threshold'] === 'number' &&
    (['escalated', 'withheld', 'below_threshold'] as readonly unknown[]).includes(candidate['verdict']) &&
    (['shadow', 'live'] as readonly unknown[]).includes(candidate['mode']) &&
    typeof candidate['escalated'] === 'boolean' &&
    (typeof candidate['incident_id'] === 'string' || candidate['incident_id'] === null) &&
    (typeof candidate['notified'] === 'boolean' || candidate['notified'] === null) &&
    (typeof candidate['notify_detail'] === 'string' || candidate['notify_detail'] === null) &&
    typeof candidate['runs'] === 'number' &&
    typeof candidate['failing_runs'] === 'number' &&
    typeof candidate['violations'] === 'number' &&
    typeof candidate['rationale'] === 'string' &&
    typeof candidate['detected_by'] === 'string' &&
    typeof candidate['detected_at'] === 'string'
  );
}

export interface DetectionPage {
  readonly detections: readonly Detection[];
  /**
   * The position to resume from, or `null` on the last page. `null` is the only honest way to say
   * "no more"; there is deliberately no total.
   */
  readonly next_cursor: string | null;
}

export function isDetectionPage(value: unknown): value is DetectionPage {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['detections']) &&
    (candidate['detections'] as readonly unknown[]).every((item) => isDetection(item)) &&
    (typeof candidate['next_cursor'] === 'string' || candidate['next_cursor'] === null)
  );
}

export interface EvaluationResult {
  readonly detections: readonly Detection[];
  /** How many opened or joined an incident. Always 0 while observing. */
  readonly escalated: number;
  /**
   * How many cleared the threshold, whether or not they escalated. During observation this is the
   * figure a reviewer watches: it is what *will* escalate once escalation is on.
   */
  readonly persistent: number;
}

export function isEvaluationResult(value: unknown): value is EvaluationResult {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['detections']) &&
    (candidate['detections'] as readonly unknown[]).every((item) => isDetection(item)) &&
    typeof candidate['escalated'] === 'number' &&
    typeof candidate['persistent'] === 'number'
  );
}

export interface ModeReport {
  readonly mode: Mode;
  /**
   * The honest name for what `shadow` means. Reported alongside `mode` rather than instead of it,
   * because the stored vocabulary is what a CHECK and existing rows use.
   */
  readonly observing: boolean;
  /**
   * When detecting began, written once by a column default. Outside `lumiq_app`'s UPDATE grant, so
   * it cannot be moved by anything holding the application's credentials — configuring the period
   * recomputes the end date and never restarts the clock.
   */
  readonly shadow_started_at: string;
  /** This tenant's configured period. Default 60. */
  readonly observation_days: number;
  /**
   * Computed from the start and the current period, never stored — a stored end date would drift
   * from the period a screen displays and would need rewriting on every change.
   */
  readonly observation_ends_at: string;
  /** Whole days left, floored at zero. An elapsed window is not "minus four days". */
  readonly days_remaining: number;
  readonly escalation_enabled_at: string | null;
  readonly enabled_by: string | null;
  /**
   * Whether escalation can be turned on now. From the platform, so a client cannot disagree with
   * the substrate about whether the door is open.
   */
  readonly can_enable: boolean;
  /** The platform's own refusal, verbatim, or `null` when the door is open. */
  readonly enable_blocked_reason: string | null;
  /**
   * Served rather than hardcoded in a client, so a form validates against the deployment's own
   * bound. The authority is a substrate CHECK; the client is never trusted to enforce it.
   */
  readonly min_observation_days: number;
  readonly max_observation_days: number;
  /**
   * When the observation period was last configured, read from this tenant's own audit chain
   * rather than a mutable column — `null` for a tenant whose period has never been changed from
   * the default, which is the ordinary state and not an error. `null` is never fabricated as
   * "changed by nobody, never".
   */
  readonly observation_period_changed_at: string | null;
  /**
   * Who last configured the observation period. `null` exactly when
   * `observation_period_changed_at` is `null`, and for no other reason.
   */
  readonly observation_period_changed_by: string | null;
}

export function isModeReport(value: unknown): value is ModeReport {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['shadow', 'live'] as readonly unknown[]).includes(candidate['mode']) &&
    typeof candidate['observing'] === 'boolean' &&
    typeof candidate['shadow_started_at'] === 'string' &&
    typeof candidate['observation_days'] === 'number' &&
    typeof candidate['observation_ends_at'] === 'string' &&
    typeof candidate['days_remaining'] === 'number' &&
    (typeof candidate['escalation_enabled_at'] === 'string' || candidate['escalation_enabled_at'] === null) &&
    (typeof candidate['enabled_by'] === 'string' || candidate['enabled_by'] === null) &&
    typeof candidate['can_enable'] === 'boolean' &&
    (typeof candidate['enable_blocked_reason'] === 'string' || candidate['enable_blocked_reason'] === null) &&
    typeof candidate['min_observation_days'] === 'number' &&
    typeof candidate['max_observation_days'] === 'number' &&
    (typeof candidate['observation_period_changed_at'] === 'string' || candidate['observation_period_changed_at'] === null) &&
    (typeof candidate['observation_period_changed_by'] === 'string' || candidate['observation_period_changed_by'] === null)
  );
}

export interface AutonomousSummary {
  readonly detections: number;
  /** Detections that cleared the threshold, escalated or withheld. */
  readonly persistent: number;
  readonly escalated: number;
  /**
   * Distinct incidents opened or joined. Lower than `escalated` whenever a condition persisted
   * across passes, which is the dedup working rather than a discrepancy.
   */
  readonly incidents: number;
  readonly notified: number;
  /**
   * Escalations that reached nobody. **The figure an operator most needs**: an incident that
   * exists and was delivered nowhere looks identical to a healthy estate on every other surface.
   */
  readonly not_routed: number;
}

export function isAutonomousSummary(value: unknown): value is AutonomousSummary {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['detections'] === 'number' &&
    typeof candidate['persistent'] === 'number' &&
    typeof candidate['escalated'] === 'number' &&
    typeof candidate['incidents'] === 'number' &&
    typeof candidate['notified'] === 'number' &&
    typeof candidate['not_routed'] === 'number'
  );
}

export interface AutonomousCapabilities {
  /** Always false. Stated as a field because it is the first thing to be clear about. */
  readonly is_an_agent: boolean;
  readonly model_involvement: string;
  /** The three things this engine does without a human. */
  readonly automatic_actions: readonly string[];
  /**
   * The list a reader most needs. Each entry is held by a mechanism rather than by this response —
   * there is no executor port, no connection and no SQL against a customer source.
   */
  readonly never_does: readonly string[];
  readonly evidence_policy: string;
  readonly confidence_formula: string;
  /**
   * 0.60. Lowered from the 0.80 that governed auto-disable, and the direction is the reason: 0.80
   * was calibrated for suppressing a signal, and escalating asks a human to look — which is cheap,
   * ignorable, and the right response to ambiguity.
   */
  readonly escalation_threshold: number;
  /** How many recent successful executions a detection reads. 12. */
  readonly evidence_window: number;
  /**
   * `autonomous.persistent_rule_failure`. Stable, because a notification template is keyed on it
   * and a dashboard groups by it — so it must not move when the wording improves.
   */
  readonly incident_cause: string;
  readonly severity_policy: string;
  readonly observation_policy: string;
  readonly modes: readonly Mode[];
  readonly verdicts: readonly DetectionVerdict[];
}

export function isAutonomousCapabilities(value: unknown): value is AutonomousCapabilities {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['is_an_agent'] === 'boolean' &&
    typeof candidate['model_involvement'] === 'string' &&
    Array.isArray(candidate['automatic_actions']) &&
    (candidate['automatic_actions'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    Array.isArray(candidate['never_does']) &&
    (candidate['never_does'] as readonly unknown[]).every((item) => typeof item === 'string') &&
    typeof candidate['evidence_policy'] === 'string' &&
    typeof candidate['confidence_formula'] === 'string' &&
    typeof candidate['escalation_threshold'] === 'number' &&
    typeof candidate['evidence_window'] === 'number' &&
    typeof candidate['incident_cause'] === 'string' &&
    typeof candidate['severity_policy'] === 'string' &&
    typeof candidate['observation_policy'] === 'string' &&
    Array.isArray(candidate['modes']) &&
    (candidate['modes'] as readonly unknown[]).every((item) => isMode(item)) &&
    Array.isArray(candidate['verdicts']) &&
    (candidate['verdicts'] as readonly unknown[]).every((item) => isDetectionVerdict(item))
  );
}
